import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { eachDayOfInterval, format, isSameDay, parseISO } from 'date-fns';
import { MissionLight } from '@wwf/sdk';
import { MissionCalendarItem } from '../MissionCalendarItem';

import { CalendarMode } from '../MissionCalendar';

interface MissionCalendarWeekViewProps {
    missions: MissionLight[];
    rangeStart: Date;
    rangeEnd: Date;
    setMode: (mode: CalendarMode) => void;
    setAnchorDate: (date: Date) => void;
}

export const MissionCalendarWeekView = ({
    missions,
    rangeStart,
    rangeEnd,
    setMode,
    setAnchorDate,
}: MissionCalendarWeekViewProps) => {
    const days = eachDayOfInterval({ start: rangeStart, end: rangeEnd });

    return (
        <Box display="flex" height="100%" minHeight={600}>
            {days.map((day, i) => {
                const isToday = isSameDay(day, new Date());

                const dayMissions = missions.filter(m => {
                    const mStart = parseISO(m.startDate!);
                    const mEnd = m.endDate ? parseISO(m.endDate) : mStart;

                    const dString = format(day, 'yyyy-MM-dd');
                    const sString = format(mStart, 'yyyy-MM-dd');
                    const eString = format(mEnd, 'yyyy-MM-dd');

                    return dString >= sString && dString <= eString;
                });

                return (
                    <Box
                        key={i}
                        onClick={() => {
                            setAnchorDate(day);
                            setMode('day');
                        }}
                        sx={{
                            flex: '1 1 0',
                            minWidth: 0,
                            width: '14.28%',
                            borderRight: i === 6 ? 0 : 1,
                            borderColor: 'divider',
                            bgcolor: isToday
                                ? 'action.hover'
                                : 'background.paper',
                            display: 'flex',
                            flexDirection: 'column',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'action.hover',
                            },
                        }}
                    >
                        {/* Day Header */}
                        <Box
                            p={2}
                            borderBottom={1}
                            borderColor="divider"
                            textAlign="center"
                            bgcolor="grey.50"
                        >
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                            >
                                {format(day, 'EEE')}
                            </Typography>
                            <Typography
                                variant="h6"
                                color={isToday ? 'primary' : 'textPrimary'}
                                fontWeight={isToday ? 'bold' : 'normal'}
                            >
                                {format(day, 'd')}
                            </Typography>
                        </Box>

                        {/* Missions List */}
                        <Box p={1} flex={1} overflow="auto">
                            {dayMissions.map(m => (
                                <MissionCalendarItem
                                    key={m.id}
                                    mission={m}
                                    viewMode="week"
                                />
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};
